import { useSelector } from "react-redux";
import { createColumnHelper } from "@tanstack/react-table";
import { ActivityIndicator } from "cpl-react-common/components/ActivityIndicators/ActivityIndicator";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload } from "@fortawesome/pro-solid-svg-icons";
import CPLTable from "cpl-react-common/components/Tables/CPLTable";
import {
  downloadDocumentFile,
  getDocumentInstances,
  selectInstances,
  selectLoadingInstances,
} from "./DocumentsTableSlice";
import { ClientPortalDocumentInstanceModel } from "services/api/cplapi/CPLAPI";
import { useThunkDispatch } from "setup/redux/thunkDispatch";
import { useEffect, useRef, useState } from "react";
import { Field, Form, Formik } from "formik";
import { useParams } from "react-router";
import translations from "utils/translationUtility";

const DocumentInstancesTable: React.FC = () => {
  const { documentID, propertyAddress } = useParams();
  const instances = useSelector(selectInstances);
  const [filteredInstances, setFilteredInstances] = useState<ClientPortalDocumentInstanceModel[]>(instances);
  const loadingInstances = useSelector(selectLoadingInstances);
  const [isDownloading, setIsDownloading] = useState<boolean>(false);

  const thunkDispatch = useThunkDispatch();
  const anchorRef = useRef<HTMLAnchorElement>(null);

  useEffect(() => {
    if (propertyAddress != null) {
      setFilteredInstances(
        instances.filter(
          (i) => i.propertyAddress !== propertyAddress || i.clientAccountNumber !== instances[0].clientAccountNumber
        )
      );
    }
  }, [instances, propertyAddress]);

  useEffect(() => {
    if (documentID != null) {
      thunkDispatch(getDocumentInstances(documentID));
    }
  }, [thunkDispatch, documentID]);

  const columnHelper = createColumnHelper<ClientPortalDocumentInstanceModel>();
  const columnDefinition = [
    columnHelper.accessor((row) => row.clientAccountNumber, {
      id: "Subject",
      cell: (info) => info.getValue() ?? "",
      header: () => <span>{translations.client} Account No</span>,
    }),
    columnHelper.accessor((row) => row.propertyAddress, {
      id: "CreatedDate",
      cell: (info) => info.getValue() ?? "",
      header: () => <span>Property Address</span>,
    }),
    columnHelper.display({
      id: "secondaryActions",
      cell: (props) =>
        props.row.original.documentID !== undefined && (
          <div className="btn-group btn-group-sm" role="group">
            <a
              className="cpl-btn btn-sm cpl-btn-outline cpl-text-primary"
              data-test-id={`document-versions-table__download-button-${props.row.original.documentID}`}
              onClick={() => downloadDocument(props.row.original.documentID!, props.row.original.filePath!)}
              data-bs-toggle="tooltip"
              data-bs-placement="top"
              title="Download Attachment"
              target="_blank"
              download
            >
              {!isDownloading && (
                <>
                  Download
                  <FontAwesomeIcon icon={faDownload} className="ms-1" />
                </>
              )}
              {isDownloading && (
                <span className="indicator-progress" style={{ display: "block" }}>
                  Downloading... <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                </span>
              )}
            </a>
          </div>
        ),
      enableHiding: false,
    }),
  ];

  async function downloadDocument(documentID: string, filePath: string) {
    setIsDownloading(true);
    const downloadedFile = await thunkDispatch(downloadDocumentFile({ documentID, filePath })).unwrap();
    if (anchorRef.current !== null) {
      const fileURL = URL.createObjectURL(downloadedFile);
      anchorRef.current.href = fileURL;
      anchorRef.current.download = downloadedFile.name;
      anchorRef.current.click();
      URL.revokeObjectURL(fileURL);
    }
    setIsDownloading(false);
  }

  return (
    <ActivityIndicator isBusy={loadingInstances}>
      <div className="card">
        <div className="card-body">
          <Formik initialValues={instances} validationSchema={{}} onSubmit={() => {}} enableReinitialize>
            <Form>
              <div className="row mb-6">
                <div className="col-sm-12 col-md-6 mb-3 mb-md-0">
                  <label className="form-label">{translations.client} Account Number</label>
                  <Field
                    className="form-control form-control-solid"
                    data-test-id="document-details__account-number-input"
                    disabled
                    value={instances[0]?.clientAccountNumber ?? ""}
                  />
                </div>
              </div>
              <div className="row mb-6">
                <div className="col-sm-12 col-md-6 mb-3 mb-md-0">
                  <label className="form-label">Description</label>
                  <Field
                    className="form-control form-control-solid"
                    data-test-id="document-details__description-input"
                    disabled
                    value={instances[0]?.description ?? ""}
                  />
                </div>
              </div>
              <div className="row mb-6">
                <div className="col-sm-12 col-md-6 mb-3 mb-md-0">
                  <label className="form-label">Document Type</label>
                  <Field
                    className="form-control form-control-solid"
                    data-test-id="document-details__document-type-input"
                    disabled
                    value={instances[0]?.documentType ?? ""}
                  />
                </div>
              </div>
              <div className="row mb-6">
                <div className="col-sm-12 col-md-6 mb-3 mb-md-0">
                  <label className="form-label">Property Address</label>
                  <Field
                    as="textarea"
                    data-test-id="document-details__property-address-textarea"
                    className="form-control form-control-solid overflow-hidden resize-none"
                    disabled
                    value={instances[0]?.propertyAddress ?? ""}
                  />
                </div>
              </div>
              <div className="row mb-6">
                <div className="col-sm-12 col-md-6 mb-3 mb-md-0">
                  <label className="form-label">Created Date</label>
                  <Field
                    className="form-control form-control-solid"
                    data-test-id="document-details__created-date-input"
                    disabled
                    value={instances[0]?.createdDate?.toCPLUserFriendlyDateString() ?? ""}
                  />
                </div>
              </div>
              <div className="row mb-6">
                <div className="col-sm-12 col-md-6 mb-3 mb-md-0">
                  <div className="btn-group btn-group-sm" role="group">
                    <a
                      className="cpl-btn btn-sm cpl-btn-outline cpl-text-primary"
                      data-test-id="document-details__download-button"
                      onClick={() => downloadDocument(documentID!, instances[0].filePath!)}
                      data-bs-toggle="tooltip"
                      data-bs-placement="top"
                      title="Download Attachment"
                      target="_blank"
                      download
                    >
                      {!isDownloading && (
                        <>
                          Download
                          <FontAwesomeIcon icon={faDownload} className="ms-1" />
                        </>
                      )}
                      {isDownloading && (
                        <span className="indicator-progress" style={{ display: "block" }}>
                          Downloading... <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                        </span>
                      )}
                    </a>
                  </div>
                </div>
              </div>
            </Form>
          </Formik>
          <h4 className="pb-0 mb-0 cpl-text-primary">Other Versions</h4>
          <div className="row mb-6">
            <div className="col-12 mb-3 mb-sm-0">
              <a ref={anchorRef} target="_blank" rel="noreferrer" className="file-preview-link" />
              <CPLTable
                data={filteredInstances}
                columns={columnDefinition}
                classNames="cpl-table-nowrap-below-desktop"
              />
            </div>
          </div>
        </div>
      </div>
    </ActivityIndicator>
  );
};

export default DocumentInstancesTable;
